/**
 * planEditorHelpers — shared utilities for the Daily Plan editor.
 *
 * getNearestProjectId(editor)
 *   Given a TipTap editor instance, return the project id of the nearest
 *   `<a href="#project:ID">` anchor that appears BEFORE the current selection.
 *   Used to auto-attach photos and material lists to the "current" project
 *   context when the admin is authoring a plan.
 *
 * splitBodyIntoTeamBlocks(html)
 *   Parse a plan body HTML string into an ordered list of team blocks. A block
 *   starts at each project anchor and captures everything up to the next
 *   project anchor. Any content BEFORE the first project anchor becomes the
 *   "intro" block (no project). Used by the drag-and-drop reorder modal so
 *   moving a project also moves the material lists / photos beneath it.
 *
 * assembleBodyFromTeamBlocks(blocks)
 *   Inverse: rebuild the HTML body from a re-ordered list of blocks.
 */

/**
 * Find the nearest project pill above the current cursor position.
 * Returns "" if no project pill precedes the cursor.
 */
export function getNearestProjectId(editor) {
  if (!editor) return "";
  const { from } = editor.state.selection;
  let projectId = "";
  editor.state.doc.nodesBetween(0, from, (node) => {
    if (node.marks) {
      for (const mark of node.marks) {
        if (mark.type.name === "link") {
          const href = mark.attrs?.href || "";
          if (href.startsWith("#project:")) {
            projectId = href.slice(9);
          }
        }
      }
    }
    return true;
  });
  return projectId;
}

/**
 * Same as above but scanning the raw HTML string. Useful when we don't have
 * a live editor instance (e.g. re-assembling body from a saved plan).
 */
export function findLastProjectIdBefore(html, offset) {
  if (!html) return "";
  const slice = offset === undefined ? html : html.slice(0, offset);
  const re = /<a[^>]*href=["']#project:([^"']+)["']/gi;
  let match; let last = "";
  while ((match = re.exec(slice)) !== null) {
    last = match[1];
  }
  return last;
}

/**
 * Parse a plan body HTML into an ordered list of team blocks.
 *
 * Result shape:
 *   [
 *     { id: "intro",     projectId: "", html: "…intro paragraphs…" },  // optional, only if content exists before the first project
 *     { id: "prjABC",    projectId: "prjABC", html: "<p><a project…></a> notes…</p><p>…more…</p>" },
 *     { id: "prjXYZ",    projectId: "prjXYZ", html: "…" },
 *   ]
 *
 * Splitting is done by scanning the top-level DOM children of the parsed body
 * and starting a new block every time we encounter a paragraph whose FIRST
 * anchor is a project pill.
 */
export function splitBodyIntoTeamBlocks(html) {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return [];

  const blocks = [];
  let current = { id: "intro", projectId: "", html: "" };

  for (const child of Array.from(root.children)) {
    // Look for a project anchor in this child. We only split when the FIRST
    // anchor of a paragraph is a project pill, so notes mentioning multiple
    // projects inline stay together.
    const firstAnchor = child.querySelector("a[href^='#project:']");
    const firstAnchorIsProject = firstAnchor &&
      // The project link must be the first inline node (ignore whitespace).
      (() => {
        // Walk the paragraph's inline children; first non-empty text/element
        // must be the project anchor.
        for (const c of child.childNodes) {
          if (c.nodeType === 3 && !c.textContent.trim()) continue;
          return c === firstAnchor || c.contains?.(firstAnchor);
        }
        return false;
      })();

    if (firstAnchorIsProject) {
      // Flush the current block
      if (current.html.trim()) blocks.push(current);
      const projectId = firstAnchor.getAttribute("href").slice(9);
      current = {
        id: projectId,
        projectId,
        html: child.outerHTML,
      };
    } else {
      current.html += child.outerHTML;
    }
  }
  if (current.html.trim()) blocks.push(current);
  return blocks;
}

/**
 * Rebuild the HTML body from a list of blocks (used after drag-and-drop).
 * Simply concatenates the HTML of each block in order.
 */
export function assembleBodyFromTeamBlocks(blocks) {
  return (blocks || []).map((b) => b.html || "").join("");
}
